import type { ChangeEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonV2, DrawerV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useTheme } from '@withjoy/joykit/theme';
import type { BoxProps, IconV2Props } from '@withjoy/joykit';
import { useFormControl } from '@withjoy/joykit/components/FormControl/FormControl.provider';
import { useLocationInputV2 } from './LocationInputV2.controller';
import type { LocationInputV2Props } from './LocationInputV2.controller';
import type { AiBorderBoxIntent } from '../AiBorderBox';
import { AiBorderBox } from '../AiBorderBox';
import { ArrowLeft, Bed, Close, MapMarker } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import type { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import type { OptionTypeV2 } from './LocationInputV2.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { PoweredByGoogle } from '../LocationInput/PoweredByGoogle';
import { animationTransition } from '@shared/utils/animationTransition';
import { useTranslation } from '@shared/core';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { inputV1ToV2Styles as sharedInputV1ToV2Styles, extractContainerStyles, StyledInputContainer } from '@shared/utils/style/inputV1ToV2Styles';
import type { FieldFocusRingColors } from '@shared/utils/style/fieldFocusRing';
import { LocationInputV2SelectV1 } from './LocationInputV2SelectV1';
import { LocationInputV2InlineLabelSelectV1 } from './LocationInputV2InlineLabelSelectV1';

interface FormikValidationProps
  extends Readonly<{
    isInvalid?: boolean;
    error?: string;
  }> {}

export const inputV1ToV2Styles = (colors: FieldFocusRingColors, { isInvalid, error }: FormikValidationProps): CSSObject => {
  const base = sharedInputV1ToV2Styles(colors, {
    isInvalid,
    error,
    focusColor: 'primary7',
    borderStrategy: 'border',
  });
  const { container, input } = extractContainerStyles(base);
  return {
    ...base,
    ['.location-input-icon']: { left: '18px' },
    [`${StyledInputContainer}`]: {
      ...container,
      ['input']: {
        ...input,
        ':disabled': { backgroundColor: colors.inputFillDisabled },
      },
    },
  };
};

const AUTO_COMPLETE_LIST_MIN_HEIGHT_PX = 400;

const AutoCompleteListItem = <T extends OptionTypeV2>({
  option,
  selectedKey,
  onSelect,
  isHideListItemIcon,
}: {
  option: T;
  selectedKey?: string;
  onSelect?: (item: T) => void;
  isHideListItemIcon?: boolean;
}) => {
  const isSelected = selectedKey === (option.key || option.value);
  const isDisabled = option.disabled;

  const handleOnClick = useEventCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!isDisabled) {
      onSelect?.(option);
    }
  });

  return (
    <Flex
      gap={4}
      width="100%"
      padding={4}
      backgroundColor={isSelected ? 'mono3' : isDisabled ? 'typeDisabledDark' : 'white'}
      _hover={{ backgroundColor: 'mono2' }}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      alignItems="center"
      onClick={handleOnClick}
      role="option"
      aria-selected={isSelected}
    >
      {!isHideListItemIcon && (option.isHotel ? <Bed size={32} /> : <MapMarker size={32} />)}
      <Box>
        <TextV2 typographyVariant="button2">{option.label}</TextV2>
        <TextV2
          typographyVariant="label2"
          color="mono10"
        >
          {option.subtitle}
        </TextV2>
      </Box>
    </Flex>
  );
};

const MemoizedAutoCompleteListItem = React.memo(AutoCompleteListItem) as typeof AutoCompleteListItem;

const AutoCompleteList = <T extends OptionTypeV2>({
  options,
  onSelect,
  noOptionsMessage,
  isHideListItemIcon,
  inputValue,
}: {
  options: ReadonlyArray<T>;
  onSelect: (item: T) => void;
  noOptionsMessage?: string;
  isHideListItemIcon?: boolean;
  inputValue?: string;
}) => {
  const [selectedKey, setSelectedKey] = useState<string>();

  const handleOnSelect = useEventCallback((item: T) => {
    setSelectedKey(item.key || item.value);
    onSelect(item);
  });

  const hasInput = !!inputValue?.trim();

  return (
    <Flex
      flexDirection="column"
      width="100%"
      minHeight={pxToRem(AUTO_COMPLETE_LIST_MIN_HEIGHT_PX)}
    >
      {options.map(option => (
        <MemoizedAutoCompleteListItem
          key={option.key || option.value}
          option={option}
          selectedKey={selectedKey}
          onSelect={handleOnSelect}
          isHideListItemIcon={isHideListItemIcon}
        />
      ))}

      {options.length === 0 && hasInput && <NoOptionsMessage noOptionsMessage={noOptionsMessage} />}

      <Box marginTop={3}>
        <PoweredByGoogle />
      </Box>
    </Flex>
  );
};

const MemoizedAutoCompleteList = React.memo(AutoCompleteList) as typeof AutoCompleteList;

const NoOptionsMessage = React.memo(({ noOptionsMessage }: { noOptionsMessage?: string }) => {
  const { t } = useTranslation('joykit');
  const message = noOptionsMessage || t('locationInput', 'noOptionsMessage')();
  return (
    <TextV2
      typographyVariant="button2"
      textAlign="center"
      marginTop={3}
    >
      {message}
    </TextV2>
  );
});

// Moved existing code in separate component
interface NormalBorderBoxProps {
  error?: string;
  isInvalid?: boolean;
  width?: BoxProps['width'];
  children?: React.ReactNode;
}

export const NormalBorderBox = ({ children, ...restProps }: NormalBorderBoxProps) => {
  const { colors } = useTheme();
  return (
    <Flex
      justifyContent="center"
      columnGap={16}
      borderRadius={pxToRem(30)} // Slightly less than outer radius
      backgroundColor="white" // Inner background
      paddingY={3}
      paddingLeft={pxToRem(18)}
      paddingRight={5}
      width="100%"
      maxWidth="448px"
      height="64px"
      zIndex="banner"
      boxShadow="0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06)"
      border="1px solid"
      borderColor="mono5"
      _hover={{ borderColor: 'primary7' }}
      _focusWithin={{
        boxShadow: `0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 0 0px 1px ${colors.primary7}`,
      }}
      transition={animationTransition('border-color', 'box-shadow')}
      marginX="auto"
      {...restProps}
    >
      {children}
    </Flex>
  );
};

type ExpandedContentProps = Readonly<{
  expandedViewTitle?: string;
  borderWrapper: typeof NormalBorderBox;
  loading?: boolean;
  isFetchingPredictions: boolean;
  options: ReadonlyArray<OptionTypeV2>;
  isHideListItemIcon?: boolean;
  noOptionsMessage?: string;
  inputValue?: string;
  children?: React.ReactNode;
  onExpandToggle: () => void;
  onReset: (e: React.MouseEvent<HTMLDivElement>) => void;
  onSelect: (item: OptionTypeV2) => void;
  renderInput: (isExpandedView?: boolean) => React.ReactNode;
}>;

const ExpandedContent: React.FC<ExpandedContentProps> = ({
  expandedViewTitle,
  borderWrapper: BorderWrapper,
  loading,
  isFetchingPredictions,
  options,
  isHideListItemIcon,
  noOptionsMessage,
  inputValue,
  children,
  onExpandToggle,
  onReset,
  onSelect,
  renderInput,
}) => {
  return (
    <Flex
      flexDirection="column"
      flex={1}
      minHeight={0}
    >
      {/* Fixed header: title + search bar (non-scrollable) */}
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={3}
        paddingTop={5}
        paddingX={5}
        flexShrink={0}
      >
        {expandedViewTitle && (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            paddingX={pxToRem(24)}
            width="100%"
          >
            <TextV2
              typographyVariant="hed2"
              textAlign="left"
            >
              {expandedViewTitle}
            </TextV2>
          </Flex>
        )}

        <BorderWrapper width="100%">
          <Flex
            width="100%"
            gap={5}
            justifyContent="space-between"
            alignItems="center"
            minHeight={pxToRem(48)}
          >
            <Flex
              height={pxToRem(40)}
              width={pxToRem(40)}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={onExpandToggle}
            >
              <ArrowLeft size={16} />
            </Flex>

            {renderInput(true)}

            {loading || isFetchingPredictions ? (
              <ButtonV2
                variant="solid"
                intent="neutral"
                shape="rounded"
                type="submit"
                width={pxToRem(48)}
                height={pxToRem(48)}
                marginRight={-3}
                display={'flex'}
                loading
              />
            ) : (
              <Flex
                height={pxToRem(40)}
                width={pxToRem(40)}
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={onReset}
              >
                <Close size={16} />
              </Flex>
            )}
          </Flex>
        </BorderWrapper>
      </Flex>

      {/* Scrollable results area */}
      <Flex
        flexDirection="column"
        flex={1}
        minHeight={0}
        overflowY="auto"
        paddingX={5}
        paddingBottom={5}
        marginTop={3}
      >
        <MemoizedAutoCompleteList
          options={options}
          onSelect={onSelect}
          isHideListItemIcon={isHideListItemIcon}
          noOptionsMessage={noOptionsMessage}
          inputValue={inputValue}
        />
        {children}
      </Flex>
    </Flex>
  );
};

export interface LocationInputV2ExpandableProps<T> extends LocationInputV2Props<T> {
  shouldCloseOnSelect?: boolean;
  intent?: AiBorderBoxIntent;
  label?: string;
  inputIcon?: React.FC<IconV2Props>;
  children?: React.ReactNode;
  isDisabled?: boolean;
  isInvalid: boolean;
  shape?: 'square' | 'rounded';
  // To show auto search prediction list in popover
  hasPopOver?: boolean;
  // To show input field variant
  inputVariant?: 'inputV2' | 'inputV2InlineLabel';
  // Props for expanded view header
  expandedViewTitle?: string;
}

export const LocationInputV2Expandable = <T extends {}>(props: LocationInputV2ExpandableProps<T>) => {
  const { loading, disabled, label, shouldCloseOnSelect, isHideListItemIcon, isInvalid, inputVariant, expandedViewTitle } = props;
  const InputIcon = props.inputIcon;
  const { options, inputValue, handleInputValue, handleChange, onLocationOpen, onLocationClose, placeholder, isFetchingPredictions, iconRight } = useLocationInputV2(props);

  const formControlProps = useFormControl({ isInvalid });
  const isMobile = useIsMobileScreen();

  const [isExpanded, setIsExpanded] = useState(false);
  const expandedInputRef = useRef<HTMLInputElement>(null);

  const isInputV2InlineLabel = inputVariant === 'inputV2InlineLabel';
  const LocationInputV2Select = isInputV2InlineLabel ? LocationInputV2InlineLabelSelectV1 : LocationInputV2SelectV1;

  const memoizedOptions = useMemo(() => options, [options]);

  const BorderWrapper = props.intent === 'aiBorder' ? AiBorderBox : NormalBorderBox;

  const handleExpandToggle = useEventCallback(() => {
    const nextState = !isExpanded;
    setIsExpanded(nextState);
  });

  const handleOnClick = useEventCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsExpanded(true);
    handleInputValue(e.currentTarget.value);
    onLocationOpen();
  });

  const handleOnSelect = useEventCallback((item: OptionTypeV2) => {
    handleInputValue(item.label);
    handleChange(item);
    if (shouldCloseOnSelect) {
      setIsExpanded(false);
    }
  });

  const handleOnChange = useEventCallback((e: ChangeEvent<HTMLInputElement>) => {
    handleInputValue(e.target.value);
  });

  const handleOnReset = useEventCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    handleInputValue('');
    expandedInputRef.current?.focus();
  });

  useEffect(() => {
    // On mobile, do NOT auto-focus the search input when the drawer opens.
    // Two focus events fight on iOS Safari: the native focus from tapping the
    // collapsed field + our programmatic focus both trigger keyboard animations,
    // causing the position:fixed drawer to scroll off the top of the screen.
    // Users naturally tap the search input themselves (standard iOS sheet UX).
    // On desktop, auto-focus is fine and improves UX.
    if (isExpanded && expandedInputRef.current && !isMobile) {
      expandedInputRef.current.focus();
    }
  }, [isExpanded, isMobile]);

  const renderInput = useCallback(
    (isExpandedView = false) => (
      <FormControl
        width="100%"
        display="flex"
        flexDirection="column"
        verticalAlign="bottom"
        label={
          label ? (
            <TextV2
              typographyVariant="label3"
              color="mono14"
            >
              {label}
            </TextV2>
          ) : undefined
        }
        flexGrow={1}
        paddingLeft={isExpandedView ? 0 : 3}
        overrides={{
          Label: { props: { marginBottom: pxToRem(2) } },
          Root: { props: { justifyContent: 'center' } },
        }}
        {...formControlProps}
        onClick={handleOnClick}
      >
        <InputV2
          ref={isExpandedView ? expandedInputRef : undefined}
          autoComplete="off"
          value={inputValue}
          onChange={handleOnChange}
          name={props.name}
          placeholder={placeholder || 'San Francisco, CA'}
          overrides={{
            Root: {
              props: {
                border: 'none',
                _after: { boxShadow: 'none' },
              },
            },
            Input: {
              props: {
                height: 'auto',
                padding: 0,
                width: '100%',
                typographyVariant: 'body2',
                color: 'mono14',
              },
            },
          }}
        />
      </FormControl>
    ),
    [formControlProps, handleOnChange, handleOnClick, inputValue, label, placeholder, props.name],
  );

  const expandedContent = (
    <ExpandedContent
      expandedViewTitle={expandedViewTitle}
      borderWrapper={BorderWrapper}
      loading={loading}
      isFetchingPredictions={isFetchingPredictions}
      options={memoizedOptions}
      isHideListItemIcon={isHideListItemIcon}
      noOptionsMessage={props.noOptionsMessage}
      inputValue={inputValue}
      onExpandToggle={handleExpandToggle}
      onReset={handleOnReset}
      onSelect={handleOnSelect}
      renderInput={renderInput}
    >
      {props.children}
    </ExpandedContent>
  );

  return (
    <>
      {/* EXPANDED VIEW FOR DESKTOP */}
      {isExpanded && !isMobile && (
        <Flex
          flexDirection="column"
          position={{ __: 'fixed', sm2: 'absolute' }}
          top={0}
          left={0}
          width="100%"
          height="100%"
          backgroundColor="white"
          zIndex="popover"
          pointerEvents={'auto'}
        >
          {expandedContent}
        </Flex>
      )}

      {/* EXPANDED VIEW FOR MOBILE */}
      <DrawerV2
        isOpen={isExpanded && isMobile}
        overrides={{
          Content: {
            props: { maxHeight: '97%', width: '100%', borderTopLeftRadius: 4, borderTopRightRadius: 4, userSelect: 'text', overflow: 'hidden', overscrollBehavior: 'contain' },
          },
        }}
        onClose={handleExpandToggle}
        anchor="bottom"
        stickyBehavior="header-footer"
        size="auto"
        useBackdrop
        disableAutoFocus
      >
        {expandedContent}
      </DrawerV2>

      <Flex
        flexDirection="column"
        width="100%"
        position={'relative'}
        gap={3}
        role={!props.hasPopOver ? 'button' : undefined}
        tabIndex={-1}
        onClick={!props.hasPopOver ? handleExpandToggle : undefined}
        onFocus={!props.hasPopOver ? handleExpandToggle : undefined}
      >
        {/* COLLAPSED VIEW */}
        {props.hasPopOver || props.shape === 'square' ? (
          /* POP OVER FIELD */
          <FormControl
            {...formControlProps}
            {...(!isInputV2InlineLabel && { label })}
          >
            <LocationInputV2Select
              key={`${props.value?.placeId}`}
              label={label}
              noBorder={props.noBorder}
              {...(!isInputV2InlineLabel && { placeholder: placeholder || 'San Francisco, CA' })}
              options={options}
              inputValue={inputValue}
              defaultValue={props.value ? { value: props.value.placeId, label: props.value.displayName, subtitle: props.value.address, isHotel: false } : undefined}
              noOptionsMessage={props.noOptionsMessage}
              searchable
              isInvalid={formControlProps.isInvalid || props.isInvalid}
              onChange={handleChange}
              onMenuOpen={onLocationOpen}
              onMenuClose={onLocationClose}
              optionFilter={() => true}
              onInputChange={handleInputValue}
              autoComplete="off"
              isHideListItemIcon={props.isHideListItemIcon}
              afterMenuContent={<PoweredByGoogle />}
              maxDropdownHeight={props.maxDropdownHeight ? props.maxDropdownHeight : pxToRem(AUTO_COMPLETE_LIST_MIN_HEIGHT_PX)}
              disabled={formControlProps.isDisabled || props.isDisabled}
              iconRight={iconRight}
            />
          </FormControl>
        ) : (
          /* EXPANDABLE FIELD */
          <BorderWrapper width="100%">
            <Flex
              width="100%"
              gap={5}
              justifyContent="space-between"
              alignItems="center"
              minHeight={pxToRem(48)}
            >
              {renderInput()}

              {(loading || isFetchingPredictions || InputIcon) && (
                <ButtonV2
                  variant="solid"
                  intent="neutral"
                  shape="rounded"
                  startIcon={<>{InputIcon && <InputIcon />}</>}
                  type="submit"
                  width={pxToRem(48)}
                  height={pxToRem(48)}
                  marginRight={-3}
                  disabled={disabled}
                  loading={loading}
                  display={'flex'}
                />
              )}
            </Flex>
          </BorderWrapper>
        )}
      </Flex>
    </>
  );
};
